"use client";

import React, {FC, useEffect, useState, useMemo, useRef} from "react";
import {useUser} from "@/shared/lib/UserContext";
import {Row} from "@tanstack/react-table";
import {AutoTableInterface} from "@/features/types/AutoTableInterface";
import {ReservationFormData} from "@/features/types/StatusChangeInterface";
import AsyncSelect from "react-select/async";
import {SelectInstance} from "react-select";
import {getAutoTableOptionsWithFilters} from "@/features/controllers/AutoTableController";
import {selectShadcnStyles} from "@/shared/lib/selectShadcnStyles";
import {Option} from "@/features/types/AutoTableFilterInterface";
import {debounce} from "@tanstack/virtual-core";

export interface ReservationActionContentProps {
    row: Row<AutoTableInterface>;
    onDataChange?: (data: ReservationFormData) => void;
}

export const ReservationActionContent: FC<ReservationActionContentProps> = ({row, onDataChange}) => {
    const {userName: currentUserName, isManager} = useUser();
    const [f_sales_user_name, setSalesUserName] = useState<string | null>(currentUserName || null);
    const [reservation_days, setReservation_days] = useState<number | null>(3);
    const [reservation_type, setReservation_type] = useState<"short" | "long" | null>(null);
    
    const [defaultDynamicOptions, setDefaultDynamicOptions] = useState<Option[]>([]);
    const [isLoadingOptions, setIsLoadingOptions] = useState(false);
    const [optionsKey, setOptionsKey] = useState(0);
    const asyncSelectRef = useRef<SelectInstance<Option> | null>(null);

    const order_num = row.original.f_ord_num || "-";

    // Pobierz dostępnych użytkowników przy montowaniu (tylko dla managera)
    useEffect(() => {
        if (isManager) {
            setIsLoadingOptions(true);
            (async () => {
                try {
                    const opts = await getAutoTableOptionsWithFilters("f_sales_user_name")({}, false);
                    setDefaultDynamicOptions(opts);
                    setOptionsKey(prev => prev + 1);
                } catch {
                } finally {
                    setIsLoadingOptions(false);
                }
            })();
        }
    }, [isManager]);

    const loadDynamicOptions = useMemo(
        () => debounce(window, async (inputValue: string, callback: (opts: Option[]) => void) => {
            try {
                const searchFilters: Record<string, string> = {};
                searchFilters["f_sales_user_name"] = inputValue || '';
                const opts = await getAutoTableOptionsWithFilters("f_sales_user_name")(searchFilters, true);
                callback(opts.slice(0, 10));
            } catch {
                callback([]);
            }
        }, 300),
        []
    );

    // Ustaw domyślnie currentUserName jeśli jest dostępny
    // Dla zwykłych użytkowników zawsze ustaw na siebie, dla managera tylko jeśli nie ma wyboru
    useEffect(() => {
        if (currentUserName) {
            if (!isManager) {
                // Zwykły użytkownik - zawsze przypisuje na siebie
                setSalesUserName(currentUserName);
            } else if (!f_sales_user_name) {
                // Manager - ustaw domyślnie, ale może zmienić
                setSalesUserName(currentUserName);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUserName, isManager]); // Uwzględnij isManager w zależnościach

    // Aktualizuj dane w rodzicu przy każdej zmianie
    useEffect(() => {
        onDataChange?.({
            f_sales_user_name,
            reservation_days,
            reservation_type,
        });
    }, [f_sales_user_name, reservation_days, reservation_type, onDataChange]);

    const canSelectShortTerm = (reservation_days ?? 0) > 0;

    // Oblicz datę końcową dla krótkoterminowej rezerwacji
    const calculateEndDate = (days: number): string => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const endDate = new Date(today);
        // Dodajemy days + 1, bo np. dla 4 stycznia + 3 dni powinno być 7 stycznia (nie 6)
        endDate.setDate(endDate.getDate() + days + 1);
        return endDate.toISOString().split('T')[0];
    };

    const formatDate = (dateStr: string): string => {
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
    };

    const endDateStr = reservation_days ? calculateEndDate(reservation_days) : null;
    const formattedEndDate = endDateStr ? formatDate(endDateStr) : null;

    return (
        <div className="w-full">
            {/* Sekcja 1: Pytanie o rezerwację */}
            <div className="mb-4">
                <div className="text-sm font-medium">
                    Czy dokonać rezerwacji bieżącego pojazdu ({order_num})?
                </div>
            </div>

            <div className="border-t border-gray-600 my-4"></div>

            {/* Sekcja 3 i 4: Typ rezerwacji (lewo) i Wybór użytkownika (prawo) */}
            <div className="flex flex-row gap-4 mb-4">
                {/* Lewa strona: Typ rezerwacji */}
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium mb-2">Typ rezerwacji:</div>
                    <div className="flex flex-col gap-2 text-sm">
                        {canSelectShortTerm && (
                            <label className="flex items-start gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="reservation_type"
                                    value="short"
                                    checked={reservation_type === "short"}
                                    onChange={(e) => setReservation_type(e.target.value as "short")}
                                    className="cursor-pointer mt-0.5 flex-shrink-0"
                                />
                                <span className="flex flex-wrap items-baseline gap-1">
                                    <span>Krótkoterminowe</span>
                                    <span className="whitespace-nowrap">
                                        ({reservation_days} {reservation_days === 1 ? "dzień" : "dni"}
                                        {formattedEndDate && ` - do ${formattedEndDate}`})
                                    </span>
                                </span>
                            </label>
                        )}
                        {isManager && (
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="reservation_type"
                                    value="long"
                                    checked={reservation_type === "long"}
                                    onChange={(e) => setReservation_type(e.target.value as "long")}
                                    className="cursor-pointer"
                                />
                                <span>Długoterminowe</span>
                            </label>
                        )}
                    </div>
                </div>

                {/* Prawa strona: Wybór użytkownika (tylko dla managera) */}
                {isManager ? (
                    <div className="w-64 flex-shrink-0">
                        <div className="text-sm font-medium mb-2">Użytkownik:</div>
                        <AsyncSelect
                            ref={asyncSelectRef}
                            key={`f_sales_user_name-${optionsKey}`}
                            placeholder="Wybierz użytkownika"
                            cacheOptions
                            defaultOptions={isLoadingOptions ? [] : defaultDynamicOptions}
                            isLoading={isLoadingOptions}
                            className="basic-single w-full text-sm"
                            styles={{
                                menuList: (base) => ({
                                    ...base,
                                    maxHeight: "200px",
                                    paddingTop: "8px",
                                    paddingBottom: "8px",
                                }),
                                menu: (base) => ({
                                    ...base,
                                    padding: "4px",
                                }),
                                ...selectShadcnStyles
                            }}
                            loadOptions={(inputValue) =>
                                new Promise((resolve) => {
                                    loadDynamicOptions(inputValue, (dynamicOpts: Option[]) => {
                                        resolve(dynamicOpts);
                                    });
                                })
                            }
                            value={f_sales_user_name ? {value: f_sales_user_name, label: f_sales_user_name} : null}
                            onChange={(opt) => setSalesUserName(opt ? opt.value : null)}
                            loadingMessage={() => "Wczytywanie..."}
                            noOptionsMessage={() => "Brak dostępnych opcji"}
                        />
                    </div>
                ) : (
                    <div className="w-64 flex-shrink-0">
                        <div className="text-sm font-medium mb-2">Użytkownik:</div>
                        <div className="text-sm text-gray-400 py-2">
                            {f_sales_user_name || currentUserName || "Brak użytkownika"}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                            Rezerwacja zostanie przypisana do Ciebie
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
